import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErrorCode } from 'react-dropzone';
import { ACCEPT_VIDEO_EXTENSIONS_MAGRITTE, MAX_VIDEO_SIZE } from 'utils/constants/banner';
import { MagritteNotificationName } from 'utils/constants/notification';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import videoButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/video_button_click';
import videoElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/video_element_shown';
import { Button, Text, Upload, VSpacing } from '@hh.ru/magritte-ui';
import type { LoadedFile } from '@hh.ru/magritte-ui';
import type { Accept } from '@hh.ru/magritte-ui-upload/node_modules/react-dropzone';

import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { loadVideo } from 'components/common/BannerVideo/loadVideo';
import { TechnicalRequirements } from 'components/common/BannerVideo/TechnicalRequirements/TechnicalRequirements';
import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';

import { resetBannerVideo, resetBannerVideoFiles, setIsVideoLoading } from 'store/models/createBanner';
import { addMagritteNotification } from 'store/models/notification';
import { resetSelectedVideo } from 'store/models/videoGallery';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    description: 'AdCreate.AdForm.BannerVideo.advice.description',
    subtitle: 'AdCreate.AdForm.BannerVideo.advice.subtitle',
    button: 'AdCreate.AdForm.BannerVideo.UploadField.button',
    select: 'AdCreate.AdForm.BannerVideo.UploadField.select',
    renderFileInfo: {
        loading: 'AdCreate.AdForm.BannerVideo.UploadField.renderFileInfo.loading',
        loaded: 'AdCreate.AdForm.BannerVideo.UploadField.renderFileInfo.loaded',
        error: 'AdCreate.AdForm.BannerVideo.UploadField.renderFileInfo.error',
    },
};

enum ForbiddenExtension {
    MOV = 'mov',
}

export const UploadField: FC = () => {
    const [uploadedFiles, setUploadedFiles] = useState<LoadedFile[]>([]);

    const bannerVacancyId = useSelector((state) => state.createBanner.bannerVacancyId);
    const vacancyId = bannerVacancyId ? String(bannerVacancyId) : null;

    const dispatch = useDispatch();
    const trl = useTrls();
    const { abortRequest, abortSignal } = useRequestCancel();
    const activeSignalRef = useRef<AbortSignal | null>(null);

    const isVideoLoading = useSelector((state) => state.createBanner.isVideoLoading);

    const handleFileUpload = useCallback(
        (file: File) => {
            videoButtonClick({
                vacancyId,
            });

            abortRequest();
            const signal = abortSignal();
            activeSignalRef.current = signal;

            void dispatch(
                loadVideo({
                    file,
                    signal,
                    callback: (isVideoLoading: boolean) => {
                        if (!isVideoLoading && activeSignalRef.current !== signal) {
                            return;
                        }
                        dispatch(setIsVideoLoading(isVideoLoading));
                    },
                    onProgress: (percentage) => {
                        setUploadedFiles((prevFiles) => {
                            if (!prevFiles.length) {
                                return prevFiles;
                            }
                            return prevFiles.map((file) => ({
                                ...file,
                                loadPercentage: percentage,
                            }));
                        });
                    },
                })
            );
        },
        [abortRequest, abortSignal, dispatch, vacancyId]
    );

    const handleSelectOnComputerClick = useCallback(() => {
        videoButtonClick({
            vacancyId,
        });
    }, [vacancyId]);

    const magritteTrls = useMemo(
        () => ({
            title: trl(TrlKeys.select),
            subTitle: trl(TrlKeys.subtitle),
        }),
        [trl]
    );

    const handleMagritteDropAccepted = useCallback(
        (files: LoadedFile[]) => {
            const file = files[0]?.data;
            if (!file) {
                return;
            }

            const fileExtension = file.name.split('.').pop();
            if (fileExtension === ForbiddenExtension.MOV) {
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormVideoWrongFormat));
                return;
            }

            setUploadedFiles([{ ...files[0], loading: true }]);
            handleFileUpload(file);
        },
        [handleFileUpload, dispatch]
    );

    const handleMagritteDropRejected = useCallback(
        (files: LoadedFile[]) => {
            const errorCode = files?.[0]?.error;
            if (errorCode === ErrorCode.FileTooLarge) {
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormVideoTooBigSize));
                return;
            }
            if (errorCode === ErrorCode.FileInvalidType) {
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormVideoWrongFormat));
            }
        },
        [dispatch]
    );

    const handleMagritteFileDelete = useCallback(() => {
        setUploadedFiles([]);
        activeSignalRef.current = null;
        abortRequest();
        dispatch([
            resetBannerVideo(null),
            resetBannerVideoFiles(null),
            resetSelectedVideo(null),
            setIsVideoLoading(false),
        ]);
    }, [abortRequest, dispatch]);

    useEffect(() => {
        setUploadedFiles((prevFiles) => {
            if (!prevFiles.length) {
                return prevFiles;
            }
            if (!isVideoLoading) {
                return [];
            }
            return prevFiles.map((file) => ({
                ...file,
                loading: true,
            }));
        });
    }, [isVideoLoading]);

    return (
        <>
            <Text typography="label-2-regular">{trl(TrlKeys.description)}</Text>
            <VSpacing default={12} />
            <TechnicalRequirements />
            <VSpacing default={24} />
            <ElementShownAnchor fn={videoElementShown} vacancyId={vacancyId}>
                <Upload
                    size="small"
                    files={uploadedFiles}
                    trls={magritteTrls}
                    accept={ACCEPT_VIDEO_EXTENSIONS_MAGRITTE as Accept}
                    maxSize={MAX_VIDEO_SIZE}
                    selectFileButton={
                        <Button mode="secondary" size="small" style="accent" onClick={handleSelectOnComputerClick}>
                            {trl(TrlKeys.button)}
                        </Button>
                    }
                    renderFileInfo={(loadedFile) => {
                        const { loading, loadPercentage, loadTime, fileSizeMB, error } = loadedFile;
                        if (error) {
                            return trl(TrlKeys.renderFileInfo.error, [error]);
                        }
                        if (loading) {
                            return trl(TrlKeys.renderFileInfo.loading, [loadPercentage, fileSizeMB]);
                        }
                        return trl(TrlKeys.renderFileInfo.loaded, [fileSizeMB, loadTime]);
                    }}
                    onDropAccepted={handleMagritteDropAccepted}
                    onDropRejected={handleMagritteDropRejected}
                    onFileDelete={handleMagritteFileDelete}
                />
            </ElementShownAnchor>
        </>
    );
};

export default withErrorBoundary(UploadField);
